import React, { useEffect, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Typography, Flex } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import DeleteEventModal from '../components/DeleteEventModal';
import FormEvent, { EventFormData } from '../components/FormEvent';

const { Title, Text } = Typography;

const EditEvent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [eventData, setEventData] = useState<EventFormData | null>(null);
  const [isPublished, setIsPublished] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { service: getEventDetailService } = useServices(ServiceNameEnum.event.detail);

  const getEventDetail = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getEventDetailService.get({ params });
    if (data) {
      setIsPublished(data.isPublished ?? false);
      setEventData({
        titleTh: data.titleML?.th || '',
        titleEn: data.titleML?.en || '',
        descriptionTh: data.descriptionML?.th || '',
        descriptionEn: data.descriptionML?.en || '',
        locationTh: data.locationML?.th || '',
        locationEn: data.locationML?.en || '',
        locationUrl: data.locationUrl || '',
        category: data.category || '',
        organizerTh: data.organizerML?.th || '',
        organizerEn: data.organizerML?.en || '',
        registrationUrl: data.registrationUrl || '',
        contactTh: data.contactML?.th || '',
        contactEn: data.contactML?.en || '',
        formatTh: data.formatML?.th || '',
        formatEn: data.formatML?.en || '',
        tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
        maxParticipants: data.maxParticipants != null ? String(data.maxParticipants) : '',
        startAt: data.startAt ? data.startAt.slice(0, 16) : '',
        endAt: data.endAt ? data.endAt.slice(0, 16) : '',
        isPublished: data.isPublished ?? false,
        galleryImages: Array.isArray(data.galleryFileIds)
          ? data.galleryFileIds.map((fid: any, idx: number) => ({
            key: `gallery-${fid}`,
            id: fid,
            type: 'image/jpeg',
            name: `gallery-${idx}`,
            size: 0,
            preview: data.galleryImageUrls?.[idx] || '',
            dimension: { width: 0, height: 0 },
          })) : [],
        coverImages: data.coverFile ? [{
          key: `cover-${data.coverFile.id}`,
          id: data.coverFile.id,
          type: data.coverFile.fileType || 'image/jpeg',
          name: data.coverFile.fileName || 'cover',
          size: 0,
          preview: data.coverFile.presignUrl || '',
          dimension: { width: 0, height: 0 },
        }] : [],
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      navigate(RouteNameEnum.event.index);
    }
  };

  useEffect(() => {
    getEventDetail();
  }, []);

  const path: PathT[] = [
    { label: 'Events', value: RouteNameEnum.event.index },
    { label: 'Edit event' },
  ];

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Title level={4} style={{ fontWeight: 'bold' }}>Edit Event</Title>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Flex gap={16} align="center">
          <Text type="secondary">status</Text>
          <StatusBadge status={isPublished} />
        </Flex>
        <Flex gap={16}>
          {checkPermission([PermissionEnum.code.eventDelete]) && (
            <AlumlyButton label="rxvWhite" icon={<DeleteOutlined />} width={200} onClick={() => setOpenDeleteModal(true)}>
              Delete event
            </AlumlyButton>
          )}
        </Flex>
      </div>
      <DeleteEventModal
        eventId={Number(id)}
        isOpen={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
      />
      {eventData && (
        <FormEvent mode="edit" eventId={id} defaultValues={eventData} />
      )}
    </div>
  );
};

export default EditEvent;
